import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Box, TextField, Typography, InputAdornment, IconButton } from '@mui/material';
import { AiOutlineClose } from 'react-icons/ai';
import inputValidation from '../utils/globalValidation';
import { fIndianAmount, readIndianAmountInput } from '../utils/formatNumber';

export default function CustomInput({
  name,
  type = 'text',
  label,
  control,
  errors,
  isRequired = false,
  placeholder,
  onChange: externalOnChange,
  multiline = false,
  disabled = false,
  isMultipleError = false,
  rows = 1,
  fieldCode = '',
  fieldType = '',
  startAdornment,
  isAmount = false,
  ...props
}) {
  const [validationError, setValidationError] = useState('');

  return (
    <Box>
      {label && (
        <Typography variant="h6" className="formLabel">
          {label} {isRequired && <span style={{ color: 'red' }}>*</span>}
        </Typography>
      )}

      <Controller
        name={name}
        control={control}
        rules={{
          ...(isRequired && { required: `${label || name} is required` }),
          validate: {
            validation: (value) => {
              const result = inputValidation({ target: { value } }, fieldCode || name, fieldType);
              return result === true || result;
            }
          }
        }}
        render={({ field: { onChange, onBlur, value, ...restField } }) => {
          const handleClear = () => {
            onChange('');
            setValidationError('');
            if (externalOnChange) {
              externalOnChange({ target: { value: '' } });
            }
          };

          const handleInputChange = (event) => {
            const amountValue = isAmount ? readIndianAmountInput(event) : undefined;
            if (amountValue === null) return;
            const e = isAmount ? { target: { value: amountValue } } : event;
            const validationResult = inputValidation(e, fieldCode || name, fieldType || type);

            setValidationError(typeof validationResult === 'string' ? validationResult : '');

            if (validationResult === true || typeof validationResult === 'string') {
              onChange(e);
              if (externalOnChange) externalOnChange(e);
            }
          };

          // Add handleBlur function
          const handleBlur = (e) => {
            onBlur(e); // Trigger RHF validation
            const validationResult = inputValidation(
              { target: { value: value || '' } },
              fieldCode || name,
              fieldType || type
            );
            setValidationError(typeof validationResult === 'string' ? validationResult : '');
          };

          let fieldError;
          if (isMultipleError) {
            const fieldKey = name.split('.').pop();
            fieldError = errors?.[fieldKey];
          } else {
            fieldError = errors?.[name];
          }

          return (
            <TextField
              {...restField}
              type={isAmount ? 'text' : type}
              fullWidth
              placeholder={placeholder || `Enter ${label}`}
              inputProps={{ className: 'textInput', ...(isAmount && { inputMode: 'decimal' }) }}
              error={!!fieldError || !!validationError}
              helperText={fieldError?.message || validationError}
              FormHelperTextProps={{ style: { color: 'red', fontWeight: 300 } }}
              value={isAmount ? (value ? fIndianAmount(value) : '') : value || ''}
              multiline={multiline}
              disabled={disabled}
              rows={rows}
              onChange={handleInputChange}
              onBlur={handleBlur} // Add onBlur handler
              InputProps={{
                startAdornment,
                endAdornment:
                  value && !disabled ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClear} edge="end">
                        <AiOutlineClose size="1rem" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
              }}
              {...props}
            />
          );
        }}
      />
    </Box>
  );
}
